import { useEffect, useRef } from "react"

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: { sitekey: string; callback: (token: string) => void; "expired-callback": () => void; "error-callback": () => void }) => string
      reset: (widgetId: string) => void
    }
  }
}

let scriptPromise: Promise<void> | null = null
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve()
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>('script[data-turnstile]')
      if (existing) {
        existing.addEventListener("load", () => resolve(), { once: true })
        existing.addEventListener("error", () => reject(new Error("Turnstile failed to load")), { once: true })
        return
      }
      const script = document.createElement("script")
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
      script.async = true
      script.defer = true
      script.dataset.turnstile = "true"
      script.onload = () => resolve()
      script.onerror = () => reject(new Error("Turnstile failed to load"))
      document.head.appendChild(script)
    })
  }
  return scriptPromise
}

export function TurnstileField({ onToken }: { onToken: (token: string) => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined
  useEffect(() => {
    if (!siteKey || !ref.current) return
    let widgetId: string | undefined
    void loadTurnstile().then(() => {
      if (!ref.current || !window.turnstile) return
      widgetId = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        callback: onToken,
        "expired-callback": () => onToken(""),
        "error-callback": () => onToken(""),
      })
    }).catch(() => onToken(""))
    return () => { if (widgetId && window.turnstile) window.turnstile.reset(widgetId) }
  }, [onToken, siteKey])
  return siteKey ? <div ref={ref} className="mt-5 min-h-16" aria-label="Security check" /> : null
}
